/**
 * "Plot on timeline" — the Trend Analysis card's link to the Trend
 * Visualization page, with the measurements the answer discussed already
 * selected on the chart.
 *
 * WHY A LINK AND NOT A BUTTON WITH A HANDLER
 * ------------------------------------------
 * It is a navigation, so it is an anchor: middle-click and "open in new tab"
 * work, the destination is visible on hover, and screen readers announce it as a
 * link. `<Link>` keeps it a client-side route change, which matters here —
 * the SMART session id lives in this tab's sessionStorage, and a full page load
 * would re-run the launch gates for no reason.
 *
 * WHEN IT DOES NOT RENDER
 * -----------------------
 * Three gates, each of which makes the destination useless rather than merely
 * empty:
 *   - the backend named no plottable measurement (no `adk_trend_viz_targets`);
 *   - the Trend Visualization UI is switched off for this build;
 *   - no patient is open, so there is no chart to open.
 * Returning null in those cases is deliberate: a link to a page that cannot
 * answer is worse than no link.
 */

import { LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useActivePatient } from '../../contexts/ActivePatientContext';
import { buildHandoffUrl, type HandoffTarget } from '../trend-viz/handoff';
import { isTrendVizUiEnabled } from '../trend-viz/flag';
import type { TrendVizTarget } from './types';

export function TrendVizHandoffButton({ targets }: { targets?: TrendVizTarget[] }) {
  const { active } = useActivePatient();
  const patientId = active?.patientId?.trim() ?? '';

  const usable: HandoffTarget[] = (targets ?? [])
    .filter((t) => typeof t?.item_id === 'string' && t.item_id.trim())
    .map((t) => ({ itemId: t.item_id.trim(), label: (t.label ?? '').trim() || t.item_id.trim() }));

  if (usable.length === 0 || !patientId || !isTrendVizUiEnabled()) return null;

  // The count is on the label because the clinician is deciding whether the
  // trip is worth it: "2 measurements" answers that, "open the chart" does not.
  const noun = usable.length === 1 ? 'measurement' : 'measurements';

  return (
    <Link
      to={buildHandoffUrl(patientId, usable)}
      data-testid="trend-viz-handoff"
      title={`Open the timeline with ${usable.map((t) => t.label).join(', ')} plotted`}
      className="inline-flex items-center gap-1.5 rounded-full border border-brand-border bg-brand-subtle px-2.5 py-1 text-[11px] font-medium text-brand-text transition-colors hover:bg-brand-subtle/60 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
    >
      <LineChart className="size-3.5" aria-hidden />
      <span>
        Plot {usable.length} {noun} on timeline
      </span>
    </Link>
  );
}
